<template>
    <div class="cash-tab">
        <skeleton-loader v-if="loading" type="cash" />

        <template v-else>
            <div class="cash-kpis">
                <kpi-card :label="$t('business.incomes')" :value="formatMoney(totalIncomes)"
                    :color="$vuetify.theme.currentTheme.success" />
                <kpi-card :label="$t('business.expenses')" :value="formatMoney(totalExpenses)"
                    :color="$vuetify.theme.currentTheme.error" />
                <kpi-card :label="$t('business.balance')" :value="formatMoney(balance)" />
                <kpi-card :label="$t('business.margin')" :value="marginLabel" />
            </div>

            <div v-if="empty" class="cash-empty">
                <span class="cash-empty-text">{{ $t('business.cash_empty') }}</span>
                <v-btn v-if="canCopyPrevious" depressed small color="primary" class="black--text"
                    :loading="copying" @click="copyPrevious">
                    {{ $t('business.copy_previous_month') }}
                </v-btn>
            </div>

            <div class="cash-cols">
                <div class="cash-block cash-block--expenses">
                    <div class="cash-block-hdr">
                        <span>{{ $t('business.expenses') }}</span>
                        <span class="cash-block-total">{{ formatMoney(totalExpenses) }}</span>
                    </div>

                    <div v-for="area in cash.expenses" :key="area.id" class="cash-area">
                        <div class="cash-area-hdr">
                            <span class="cash-area-name">{{ area.name }}</span>
                            <span class="cash-area-total">{{ formatMoney(sumArea(area)) }}</span>
                            <v-menu offset-y left nudge-bottom="4" transition="slide-y-transition">
                                <template v-slot:activator="{ on: menu, attrs: menuAttrs }">
                                    <button-tooltip top :tooltip="$t('common.options')">
                                        <template v-slot:activator="{ on: tooltip, attrs }">
                                            <button type="button" class="cash-area-more"
                                                v-bind="{ ...menuAttrs, ...attrs }" v-on="{ ...menu, ...tooltip }">
                                                <v-icon size="15">mdi-dots-horizontal</v-icon>
                                            </button>
                                        </template>
                                    </button-tooltip>
                                </template>
                                <v-list dense color="surface" class="py-1">
                                    <v-list-item @click="askRenameArea(area)">
                                        <v-list-item-title class="cash-menu-item">
                                            {{ $t('business.rename') }}
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="askDeleteArea(area)">
                                        <v-list-item-title class="cash-menu-item cash-menu-item--danger">
                                            {{ $t('common.delete') }}
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>

                        <cash-row v-for="item in area.items" :key="item.id" :name="item.name" :amount="item.amount"
                            @change="setAmount(item, $event)" @rename="askRenameItem(area, item)"
                            @delete="askDeleteItem(area, item)" />

                        <button type="button" class="cash-add-row" @click="askNewConcept(area)">
                            {{ $t('business.add_concept') }}
                        </button>
                    </div>

                    <button type="button" class="cash-add-area" @click="askNewArea">
                        {{ $t('business.add_area') }}
                    </button>
                </div>

                <div class="cash-block cash-block--incomes">
                    <div class="cash-block-hdr">
                        <span>{{ $t('business.incomes') }}</span>
                        <span class="cash-block-total">{{ formatMoney(totalIncomes) }}</span>
                    </div>

                    <cash-row :name="$t('business.clients_income')" :amount="clientsIncome" locked
                        :locked-hint="$t('business.clients_income_hint')" />

                    <cash-row v-for="item in cash.incomes" :key="item.id" :name="item.name" :amount="item.amount"
                        @change="setAmount(item, $event)" @rename="askRenameIncome(item)"
                        @delete="askDeleteIncome(item)" />

                    <button type="button" class="cash-add-row" @click="askNewIncome">
                        {{ $t('business.add_income') }}
                    </button>
                </div>
            </div>
        </template>

        <cash-entry-dialog ref="entry" @save="onSaveEntry" />
        <kimsa-confirm ref="confirm" :title="$t('business.delete_entry_title')" :content="deleteContent"
            :persistent="false" show-cancel alert-mode @confirm="onConfirmDelete" />
    </div>
</template>

<script>
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KpiCard from "@/components/common/KpiCard";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import CashRow from "@/components/company/business/CashRow";
import CashEntryDialog from "@/components/company/business/CashEntryDialog";
import { defaultCash, formatMoney, newId, sumArea, sumExpenses, sumIncomes } from "@/utils/Business";

export default {
    name: "CashTab",
    components: { SkeletonLoader, KpiCard, ButtonTooltip, KimsaConfirm, CashRow, CashEntryDialog },
    props: {
        value: {
            type: Object,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        copying: {
            type: Boolean,
            default: false,
        },
        canCopyPrevious: {
            type: Boolean,
            default: false,
        },
        saved: {
            type: Boolean,
            default: false,
        },
        clientsIncome: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            cash: defaultCash(),
            entryMode: 'area',
            target: null,
            pendingDelete: null,
        }
    },
    computed: {
        totalExpenses(vm = this) {
            return sumExpenses(vm.cash)
        },
        totalIncomes(vm = this) {
            return sumIncomes(vm.cash, vm.clientsIncome)
        },
        balance(vm = this) {
            return vm.totalIncomes - vm.totalExpenses
        },
        marginLabel(vm = this) {
            if (!vm.totalIncomes) return vm.$t('common.without_data')
            return `${Math.round(vm.balance / vm.totalIncomes * 100)}%`
        },
        empty(vm = this) {
            return !vm.saved && vm.totalExpenses === 0 && vm.totalIncomes === 0
        },
        deleteContent(vm = this) {
            return vm.$t('business.delete_entry_body', { name: vm.pendingDelete?.name || '' })
        },
    },
    created() {
        this.syncFromValue()
    },
    methods: {
        formatMoney,
        sumArea,
        syncFromValue() {
            const vm = this
            vm.cash = vm.value ? JSON.parse(JSON.stringify(vm.value)) : defaultCash()
        },
        persist() {
            this.$emit('change', this.cash)
        },
        setAmount(item, amount) {
            const vm = this
            item.amount = amount
            vm.persist()
        },
        copyPrevious() {
            this.$emit('copy:previous')
        },
        askNewArea() {
            const vm = this
            vm.entryMode = 'new-area'
            vm.target = null
            vm.$refs.entry.show('area')
        },
        askNewConcept(area) {
            const vm = this
            vm.entryMode = 'new-concept'
            vm.target = area
            vm.$refs.entry.show('concept')
        },
        askNewIncome() {
            const vm = this
            vm.entryMode = 'new-income'
            vm.target = null
            vm.$refs.entry.show('income')
        },
        askRenameArea(area) {
            const vm = this
            vm.entryMode = 'rename'
            vm.target = area
            vm.$refs.entry.show('rename', area.name)
        },
        askRenameItem(area, item) {
            const vm = this
            vm.entryMode = 'rename'
            vm.target = item
            vm.$refs.entry.show('rename', item.name)
        },
        askRenameIncome(item) {
            const vm = this
            vm.entryMode = 'rename'
            vm.target = item
            vm.$refs.entry.show('rename', item.name)
        },
        onSaveEntry({ name, amount }) {
            const vm = this
            if (vm.entryMode === 'rename' && vm.target) vm.target.name = name
            if (vm.entryMode === 'new-area') vm.cash.expenses.push({ id: newId(), name, items: [] })
            if (vm.entryMode === 'new-concept' && vm.target) {
                vm.target.items.push({ id: newId(), key: null, name, amount })
            }
            if (vm.entryMode === 'new-income') vm.cash.incomes.push({ id: newId(), name, amount })
            vm.persist()
        },
        askDeleteArea(area) {
            const vm = this
            vm.pendingDelete = { type: 'area', name: area.name, id: area.id }
            vm.$refs.confirm.show()
        },
        askDeleteItem(area, item) {
            const vm = this
            vm.pendingDelete = { type: 'item', name: item.name, id: item.id, areaId: area.id }
            vm.$refs.confirm.show()
        },
        askDeleteIncome(item) {
            const vm = this
            vm.pendingDelete = { type: 'income', name: item.name, id: item.id }
            vm.$refs.confirm.show()
        },
        onConfirmDelete() {
            const vm = this
            const pending = vm.pendingDelete
            if (!pending) return

            if (pending.type === 'area') {
                vm.cash.expenses = vm.cash.expenses.filter(area => area.id !== pending.id)
            }
            if (pending.type === 'item') {
                const area = vm.cash.expenses.find(item => item.id === pending.areaId)
                if (area) area.items = area.items.filter(item => item.id !== pending.id)
            }
            if (pending.type === 'income') {
                vm.cash.incomes = vm.cash.incomes.filter(item => item.id !== pending.id)
            }

            vm.pendingDelete = null
            vm.persist()
        },
    },
    watch: {
        value() {
            this.syncFromValue()
        },
    },
}
</script>

<style scoped>
.cash-kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin-bottom: 16px;
}

.cash-empty {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 14px;
    margin-bottom: 16px;
    border: 1px dashed var(--v-outline-base);
    border-radius: 12px;
}

.cash-empty-text {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.cash-cols {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: start;
}

.cash-block {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
}

.cash-block-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 11px 14px;
    font-size: var(--fs-caption);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v-txtSecondary-base);
}

.cash-block--expenses .cash-block-total {
    color: var(--v-error-base);
}

.cash-block--incomes .cash-block-total {
    color: var(--v-success-base);
}

.cash-block-total {
    font-size: var(--fs-body);
    font-weight: 700;
}

.cash-area-hdr {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    background: var(--v-surface2-base);
    border-top: 1px solid var(--v-outline-base);
}

.cash-area-name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v-txtSecondary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cash-area-total {
    font-size: var(--fs-caption);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.cash-area-more {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 6px;
    color: var(--v-txtMuted-base);
}

.cash-area-more:hover {
    background: var(--v-surface-base);
    color: var(--v-txtPrimary-base);
}

.cash-add-row,
.cash-add-area {
    width: 100%;
    padding: 9px 14px;
    text-align: left;
    border-top: 1px solid var(--v-outline-base);
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    transition: color 0.15s, background 0.15s;
}

.cash-add-row:hover,
.cash-add-area:hover {
    color: var(--v-primary-base);
    background: var(--v-surface2-base);
}

.cash-add-area {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.cash-menu-item {
    font-size: var(--fs-caption);
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.cash-menu-item--danger {
    color: var(--v-error-base);
}

.theme--light .cash-add-row:hover,
.theme--light .cash-add-area:hover {
    color: var(--v-accent-base);
}

@media (max-width: 960px) {
    .cash-cols {
        grid-template-columns: 1fr;
    }

    .cash-kpis {
        grid-template-columns: repeat(2, 1fr);
    }
}
</style>
